import React from 'react';
import { IndianRupee, Receipt } from 'lucide-react';

import { Colors, tone } from '@/components/phone/theme';

/**
 * The job work ledger as cards.
 *
 * Eleven columns of arithmetic do not fit a handset. A card carries the same
 * four answers per job — what went, what came back, what was agreed, what was
 * billed — with the same Bill button on the ones still waiting for one.
 *
 * Only the theme is imported: the ledger is also rendered on its own, and the
 * shared primitives bring the router with them.
 */

function Line({ label, value, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-[11px] font-medium" style={{ color: Colors.text.secondary }}>{label}</span>
      <span className="text-[13px] font-semibold tabular-nums" style={{ color: tone || Colors.text.primary }}>{value}</span>
    </div>
  );
}

export default function PhoneLedger({ rows, money, kg, onBill }) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 text-center">
        <Receipt className="h-12 w-12" style={{ color: Colors.text.disabled }} strokeWidth={1.5} />
        <p className="mt-5 text-[15px] font-medium" style={{ color: Colors.text.secondary }}>
          Nothing has gone out to a contractor yet.
        </p>
      </div>
    );
  }
  const min = tone('neutral');
  return (
    <ul data-testid="ledger-list">
      {rows.map((row) => {
        const settled = row.quantity.settled;
        const billed = row.bill.billed_received;
        return (
          <li
            key={row.tracking_id}
            data-testid={`ledger-card-${row.tracking_id}`}
            className="mt-3 rounded-[10px] border px-4 py-3"
            style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
          >
            <p className="text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>{row.vendor_name}</p>
            <p className="mt-0.5 truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
              {row.order_id}{row.process ? ` · ${row.process}` : ''}
            </p>

            <div className="mt-2 border-t pt-1" style={{ borderColor: Colors.border.subtle }}>
              <Line label="Sent" value={row.quantity.sent} />
              <Line label="Back" value={settled ? row.quantity.received : '—'} />
              <Line label="Short" value={settled ? row.quantity.short : '—'}
                tone={settled && row.quantity.short > 0 ? Colors.status.error : undefined} />
              {!!row.material.issued_kg && (
                <>
                  <Line label="Material out" value={kg(row.material.issued_kg)} />
                  <Line label="Unaccounted" value={`${kg(row.material.unaccounted_kg)} (${row.material.unaccounted_pct}%)`}
                    tone={row.material.unaccounted_kg > 0 ? Colors.semantic.caution : undefined} />
                </>
              )}
              <Line
                label="Agreed"
                value={(
                  <span className="flex items-center gap-1.5">
                    {money(row.expected_charge?.amount)}
                    {row.expected_charge?.minimum_applied && (
                      <span
                        className="rounded-md border px-2 py-[3px] text-[11px] font-semibold"
                        style={{ backgroundColor: min.bg, borderColor: min.border, color: min.fg }}
                      >
                        min
                      </span>
                    )}
                  </span>
                )}
              />
              <Line
                label="Billed"
                value={billed
                  ? `${money(row.bill.billed)}${row.bill.variance !== 0 ? ` (${row.bill.variance > 0 ? '+' : ''}${row.bill.variance_pct}%)` : ''}`
                  : '—'}
                tone={billed && row.bill.variance > 0 ? Colors.status.error : undefined}
              />
            </div>

            {!billed && (
              <button
                type="button"
                data-testid={`bill-${row.tracking_id}`}
                onClick={() => onBill(row)}
                className="mt-2 flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] border text-[13px] font-semibold"
                style={{ borderColor: Colors.accent.DEFAULT, color: Colors.accent.DEFAULT }}
              >
                <IndianRupee className="h-4 w-4" /> Record bill
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
